import { Component, DestroyRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

import { GridViewGroupingConfiguration } from '@cds/betting-offer/grouping/grid-view';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { EpcotConfig, ImagesConfig, ScoreboardBackgroundConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { StringDictionary, isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { CompetitionLogosService } from '@frontend/sports/competition-logos/feature';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { GridSubscriptionService } from '@frontend/sports/event-subscription/feature/grid-subscription';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { MediaQueryService } from '@frontend/vanilla/core';
import { clone } from 'lodash-es';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';

import { ImageQualityService } from '../common/image-quality.service';
import { Promotion, TrackingSource } from '../crm-offer-data/crm-offer.model';
import { CrmPromotionProviderService } from '../crm-promotion/crm-promotion-provider.service';
import { ComponentProxy } from '../deferred/component-loader.service';
import { HeaderContentDensity } from '../event-details-common/event-details-common.models';
import { HeaderNavigationService } from '../event-details-common/header-navigation/header-navigation.service';
import { ScoreboardSubscriptionService } from '../event-subscription/scoreboard-subscription.service';
import { HighLightsMarqueeBinding, HighlightsMarqueeComponentLoaderService } from '../highlights-marquee/highlights-marquee-component-loader.service';
import { HighlightsMarqueePage } from '../highlights-marquee/highlights-marquee-page.enum';
import { Tile } from '../highlights-marquee/marquee-tile.model';
import { MarqueeTileService } from '../highlights-marquee/marquee-tile.service';
import { MarqueeService } from '../highlights-marquee/marquee.service';
import { TabBarItem } from '../tab-bar/tab-bar.models';
import { EventLoadService } from './event-load.service';
import { LiveAlertManager } from './live-alert/live-alert-manager.service';
import { FeatureTab, LiveAlertMessage, ShowcaseEventModel, ShowcaseModuleData } from './showcase.model';
import { ShowcaseService } from './showcase.service';
import { ShowcaseTrackingService } from './showcase.tracking.service';

@Component({
    selector: 'ms-showcase',
    templateUrl: 'showcase.component.html',
    providers: [ShowcaseTrackingService],
    standalone: false,
})
export class ShowcaseComponent implements OnInit, OnDestroy, OnChanges {
    collapse = false;
    title: string;
    animationAlert: LiveAlertMessage;
    showHeaderContent = false;
    page = HighlightsMarqueePage.Lobby;
    showModule = false;
    selectedEvent: EventModel;
    mediaEvent: EventModel;
    feature?: TabBarItem;
    crmBannerPromotions: Promotion[] = [];
    tiles$ = new BehaviorSubject<Tile[]>([]);
    showCompetitionLogo: boolean;
    gridGroup?: { [key: string]: string };
    scoreboardBgColor = { 'background-color': '' };
    private eventSubscriptions?: EventSubscription<EventModel>;
    @Input() moduleData: ShowcaseModuleData;
    @Input() tracking: string;
    @Output() showcaseDataLoaded = new EventEmitter<void>();
    @HostBinding('class') className = 'showcase-container';
    @HostBinding('style.height.px') height?: number;
    @HostBinding('style.max-height.px') maxHeight?: number;

    bgImage?: SafeStyle;

    sizeDensity = HeaderContentDensity;

    tabContainerClasses: StringDictionary<string> = {
        [SportConstant.Soccer]: 'stats-container',
    };

    tabContainerBackgroundClasses: StringDictionary<string> = {
        [SportConstant.Soccer]: 'stats-background',
    };

    groupingConfig: GridViewGroupingConfiguration | undefined;

    epcotEnabled = inject(EpcotConfig).isEnabled;

    events: ShowcaseEventModel[] = [];
    marqueeComponent: ComponentProxy<HighLightsMarqueeBinding>;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private gridSubscriptionService: GridSubscriptionService,
        private scoreboardSubscriptionService: ScoreboardSubscriptionService<EventModel>,
        private redirectHelperService: RedirectHelperService,
        private headerNavigation: HeaderNavigationService,
        private showcaseService: ShowcaseService,
        private sanitizer: DomSanitizer,
        public liveAlertManager: LiveAlertManager,
        private marqueeService: MarqueeService,
        private marqueeTileService: MarqueeTileService,
        private mediaService: MediaQueryService,
        private crmPromotionProvider: CrmPromotionProviderService,
        private showcaseTrackingService: ShowcaseTrackingService,
        private competitionLogoService: CompetitionLogosService,
        private eventLoadService: EventLoadService,
        private grouping: OfferGroupingService,
        private scoreboardBackgrounds: ScoreboardBackgroundConfig,
        private imagesConfig: ImagesConfig,
        private imageQualityService: ImageQualityService,
        private marqueeLoader: HighlightsMarqueeComponentLoaderService,
    ) {
        this.marqueeComponent = this.marqueeLoader.getHighlightsMarqueeComponent();
    }

    ngOnInit(): void {
        this.headerNavigation.selectedTab.pipe(filter(isDefined), takeUntilDestroyed(this.destroyRef)).subscribe((x) => this.featureTabSelected(x));
        this.showcaseService.selectedEvent
            .pipe(
                distinctUntilChanged(),
                map((eventId: string) => this.moduleData.events.find((showcaseEvent) => showcaseEvent.event.id === eventId)),
                switchMap((eventModel) => {
                    return this.eventLoadService.getFullFixture(eventModel!, this.showcaseService.getGridGroupIds(eventModel?.gridGroup!));
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((response) => this.processResponse(response.event, response.isUpdatedEvent));

        this.mediaService
            .observe()
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.screenWidthChanged());
    }

    ngOnChanges(changes: ISimpleChanges<ShowcaseComponent>): void {
        if (changes.moduleData && this.moduleData?.events) {
            this.title = this.moduleData.title;
            this.events = this.moduleData.events;
            this.resubscribe();
            this.tabSelected();
            this.collapse = false;
            this.liveAlertManager.emptyList();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribePushSubscription();
        this.liveAlertManager.emptyList();
    }

    private featureTabSelected(feature: TabBarItem): void {
        this.checkForMediaUpdates();
        this.feature = feature;
        this.tabChanged();
    }

    private screenWidthChanged = (): void => {
        this.showHeaderContent = this.mediaService.isActive('lt-lg');
    };

    goToEvent(): void {
        if (!this.showHeaderContent || this.feature?.id === FeatureTab.Scoreboard) {
            this.redirectHelperService.goToEvent(this.selectedEvent);
            this.showcaseTrackingService.trackEventPageClick(this.selectedEvent, this.selectedEventPosition);
        }
    }

    getClass(classMap: StringDictionary<string>, key?: string | number) {
        if (key && classMap[key]) {
            return classMap[key];
        }

        return undefined;
    }

    get selectedEventPosition(): number {
        const index = this.events.findIndex((e) => e.event.id === this.selectedEvent?.id);

        return index ?? 0;
    }

    private updateClasses(): void {
        const defaultClass = 'showcase-container';
        const isStarted = this.selectedEvent.scoreboard?.started;
        const newClass = isStarted ? 'live' : 'prematch';
        this.className = [defaultClass, newClass].join(' ');
    }

    private tabChanged(): void {
        if (this.selectedEvent) {
            const background = this.scoreboardBackgrounds.backgrounds.find((x) => x.sportId === this.selectedEvent.sport.id);
            if (this.feature?.id === FeatureTab.Simulation && background) {
                this.scoreboardBgColor = { 'background-color': background.outerColor };
            } else {
                this.scoreboardBgColor = { 'background-color': '' };
            }

            this.showcaseService.setParticipantsShirtColor(this.selectedEvent);
        }
    }

    private checkAndEnableShowCaseModule(): boolean {
        this.showModule = true;
        if (this.events.length === 0) {
            this.showModule = false;
            this.unsubscribePushSubscription();
        }

        return this.showModule;
    }

    tabSelected(event?: EventModel): void {
        if (!this.checkAndEnableShowCaseModule()) {
            this.showcaseDataLoaded.emit();

            return;
        }

        event = event || this.moduleData.events[0].event;

        const selectedEvent = this.moduleData.events.find((showcaseEvent) => showcaseEvent.event.id === event?.id);

        if (selectedEvent) {
            this.grouping
                .getGridGrouping(selectedEvent.event.sport.id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((groupings) => {
                    this.groupingConfig = groupings;
                    const url = this.showcaseService.getBackgroundImageUrl(selectedEvent.event.sport.id);
                    this.bgImage = this.getSanitizedImageUrl(url);

                    this.showCompetitionLogo =
                        this.competitionLogoService.hasLogo(selectedEvent.event.sport.id, selectedEvent.event.league.id) ||
                        !!selectedEvent.event.league?.imageProfile?.logo;
                    this.gridGroup = selectedEvent.gridGroup;
                    this.selectedEvent = selectedEvent.event;
                    this.mediaEvent = clone(selectedEvent.event);

                    this.updateClasses();
                    this.loadPromotions(this.selectedEvent.id);
                });
        }
    }

    private loadPromotions(eventId: string): void {
        if (!this.moduleData.showSpecialContents) {
            this.showcaseDataLoaded.emit();

            return;
        }
        const marqueeAvailable = this.marqueeTileService.hasForFixture(eventId);
        const marqueeTiles$ = marqueeAvailable ? this.marqueeService.loadMarquees(eventId) : of(undefined);

        combineLatest([marqueeTiles$, this.crmPromotionProvider.promotions$])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([marqueeTiles, promotions]) => {
                marqueeTiles = marqueeTiles || [];
                this.tiles$.next(marqueeTiles);
                this.crmBannerPromotions = this.showcaseService.promotionChanged(promotions, this.selectedEvent);
                this.marqueeComponent.update({
                    tiles: this.tiles$.value,
                    promotionTiles: this.crmBannerPromotions,
                    showLoader: true,
                    page: this.page,
                    promotionTrackingSource: TrackingSource.ShowCase,
                });
                this.showcaseDataLoaded.emit();
            });
    }

    private getSanitizedImageUrl(url?: string): SafeStyle | undefined {
        if (!url) {
            return undefined;
        }

        if (this.imagesConfig.imageQualityParams?.homePage?.showCase)
            url = this.imageQualityService.setImageQuality(url, this.imagesConfig.imageQualityParams.homePage.showCase);

        const appendValue = url.includes('?') ? '&p=width540' : '?p=width540';

        return this.sanitizer.bypassSecurityTrustStyle(`url(${url}${appendValue}`);
    }

    densityToggled(density: HeaderContentDensity): void {
        if (density !== HeaderContentDensity.Fullscreen) {
            this.tabChanged();
        }
    }

    toggle(): void {
        this.collapse = !this.collapse;
        this.showcaseService.eventSelectedFromTabBar = false;
        this.showcaseTrackingService.trackHeaderExpandOrCollapseClick(this.selectedEvent, this.collapse);
    }

    private unsubscribePushSubscription(): void {
        this.eventSubscriptions?.unsubscribe();
    }

    private resubscribe(): void {
        this.unsubscribePushSubscription();

        const callbacks = this.events.map((model, index) => ({
            event: clone(model.event),
            callback: this.getCallback(index),
        }));

        if (callbacks.length > 0) {
            this.eventSubscriptions = this.gridSubscriptionService.subscribe(callbacks);
            this.eventSubscriptions.add(this.scoreboardSubscriptionService.subscribe(callbacks));
        }
    }

    private getCallback(index: number): (updatedEvent: EventModel) => void {
        return (updatedEvent) => {
            if (!updatedEvent?.scoreboard) {
                return;
            }
            const oldShowcaseEvent = this.events.find((f) => f.event.id === updatedEvent.id);
            if (!oldShowcaseEvent?.event) {
                return;
            }
            const oldEvent = clone(oldShowcaseEvent.event);
            const newEvent = clone(updatedEvent);
            if (updatedEvent.scoreboard.messages && updatedEvent.optionGroups.length) {
                this.moduleData.events[index].event = newEvent;
            }
            if (updatedEvent.scoreboard.messages) {
                this.moduleData.events[index].event.scoreboard.messages = newEvent.scoreboard.messages;
                this.liveAlertManager.add(updatedEvent, oldEvent);
                if (updatedEvent.id === this.selectedEvent.id) {
                    this.checkForMediaUpdates(newEvent);
                }
            }
            if (updatedEvent.optionGroups.length) {
                this.moduleData.events[index].event.optionGroups = newEvent.optionGroups;
            }
            if (updatedEvent.id === this.selectedEvent.id) {
                this.selectedEvent = newEvent;
            }
        };
    }

    private checkForMediaUpdates(newEvent?: EventModel): void {
        if (this.feature && this.feature.id !== 2) {
            this.mediaEvent = newEvent || this.selectedEvent;
        }
    }

    private processResponse = (event: EventModel, isUpdatedEvent: boolean): void => {
        this.moduleData.events.forEach((e) => {
            e.active = false;
            if (e.event.id === event.id) {
                e.active = true;
                e.event.optionGroups = event.optionGroups;
            }
        });
        if (isUpdatedEvent) {
            this.resubscribe();
        }
        this.tabSelected(event);
    };
    protected readonly TrackingSource = TrackingSource;
}
